import { PLAN_SKU, SUBSCRIPTION_STATUSES } from '@/constants'
import { formatPrecision } from '@/utils/forms'
import { formatDate } from '@/utils/date'

export const getTrialName = (base) => (
  base.status !== SUBSCRIPTION_STATUSES.EXPIRED
  && base.plan.isTrial
  && new Date(base.activationDate) > new Date()
  && !base.name?.toLowerCase().includes('trial')
    ? base.name.concat(' Trial') : base.name
)

const isAnnual = plan => plan.frequency === 'yearly'

const getMonthlyAmount = (plan) => (isAnnual(plan)
  ? formatPrecision(plan.amount / 12, 2)
  : formatPrecision(plan.amount, 2))

const getPlanFeatures = plan => plan.modules?.map(
  module => module.features?.map((feature) => feature.description),
).flat() || []

export const formatPlan = (plan) => ({
  ...plan,
  isAnnual: isAnnual(plan),
  monthlyDisplayAmount: getMonthlyAmount(plan),
  purchaseDisplayAmount: formatPrecision(plan.amount, 2),
})

const hasOnlyNeatGroup = planSku => [
  PLAN_SKU.NEAT_YEARLY,
  PLAN_SKU.NEAT_MONTHLY,
  PLAN_SKU.NEAT_YEARLY_TRIAL,
  PLAN_SKU.NEAT_INVOICES,
].includes(planSku)

export const shouldShowFixedPlans = planSku => [
  PLAN_SKU.NBOOKMO,
  PLAN_SKU.FILES_ANNUAL,
  PLAN_SKU.FILES_ANNUAL_TRIAL,
  PLAN_SKU.NEAT_MONTHLY,
].includes(planSku)

export const getPlansGroups = (planGroup) => {
  const groups = []

  if (!planGroup.length) return []

  const [base, ...plans] = planGroup

  if (shouldShowFixedPlans(base?.sku)) {
    plans.map(plan => groups.push(
      {
        name: plan.name,
        plans: [formatPlan(plan)],
        features: getPlanFeatures(plan),
        planTransitions: base.availablePlanTransitions,
      },
    ))
  } else if (plans.length === 2) {
    groups.push({
      name: 'Neat™',
      plans: plans.map(formatPlan),
      features: getPlanFeatures(plans[0]),
      planTransitions: base.availablePlanTransitions,
    })
  } else {
    const formattedPlans = { files: [], neat: [] }

    plans.forEach(plan => {
      if ([PLAN_SKU.FILES_MONTHLY, PLAN_SKU.FILES_ANNUAL].includes(plan.sku)) {
        formattedPlans.files.push(formatPlan(plan))
      } else {
        formattedPlans.neat.push(formatPlan(plan))
      }
    })

    groups.push(
      { name: 'NeatFiles™', plans: formattedPlans.files, features: getPlanFeatures(formattedPlans.files[0]), planTransitions: base.availablePlanTransitions },
      { name: 'Neat™', plans: formattedPlans.neat, features: getPlanFeatures(formattedPlans.neat[0]), planTransitions: base.availablePlanTransitions },
    )
  }

  return groups
}

export const getSkuGroup = (planSku) => [
  ...(!hasOnlyNeatGroup(planSku) ? [
    PLAN_SKU.FILES_MONTHLY, PLAN_SKU.FILES_ANNUAL,
  ] : []),
  PLAN_SKU.NEAT_MONTHLY,
  PLAN_SKU.NEAT_YEARLY,
]

export const getNextBillDate = (baseSubscription = {}, renewalPreview, showBillingForTrial = false) => {
  const nextBillingDate = formatDate(renewalPreview?.dueDate)

  if (baseSubscription.plan.isTrial) {
    if (
      baseSubscription.status === SUBSCRIPTION_STATUSES.ACTIVE
      && (new Date(baseSubscription.activationDate) < new Date() || showBillingForTrial)
    ) {
      return { nextBillingDate }
    }

    return {
      trialEndDate: formatDate(baseSubscription.activationDate ?? baseSubscription.endDate),
    }
  }

  if (baseSubscription.status === SUBSCRIPTION_STATUSES.CANCELED) {
    return {
      expiringOn: formatDate(baseSubscription.endDate),
    }
  }

  if (baseSubscription.status === SUBSCRIPTION_STATUSES.EXPIRED) {
    return {
      expired: formatDate(baseSubscription.endDate),
    }
  }

  return { nextBillingDate }
}

export default {}
